/**
 * ActualDatePickerBody — popover content shared by the row's Actual cell
 * picker (UnifiedScheduleView.renderActualOnlyDatePicker) and the
 * bottom panel's Actual Start / Actual Finish fields (ActivityBottomPanel).
 *
 * Both surfaces previously rendered slightly different popover content
 * (different width, footer, buttons). This component is the single source
 * of truth for the actual-picker UX so they cannot drift again. Trigger
 * design is intentionally NOT included here — row uses a compact button
 * (h-6 w-20), panel uses a full-width button (h-9). Only the popover
 * BODY is shared.
 *
 * Props:
 *   initialValue        — current actual_start or actual_finish (ISO string or null)
 *   field               — "actual_start" | "actual_finish"
 *   activity            — for actual_start reference when field=actual_finish (constraint #9)
 *   projectDataDate     — string ISO date or null (used to compute max picker date)
 *   onSave              — (formattedDate: string, comment: string) => void
 *   onCancel            — () => void
 *   saving              — loading state for the Save button
 *   initialComment      — optional starting comment text
 *   testIdPrefix        — defaults to "actual-picker" so existing test selectors keep working
 *
 * Defined at MODULE level + wrapped in React.memo. Inline component
 * definitions inside parent functions destroy popover identity across
 * parent re-renders — this is the bouncing bug we fixed in the prior PR.
 */
import React, { useState, useMemo, useCallback } from "react";
import { format, parseISO } from "date-fns";
import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Calendar as CalendarComponent } from "../ui/calendar";

const ActualDatePickerBody = React.memo(function ActualDatePickerBody({
  initialValue,
  field,
  activity,
  projectDataDate,
  onSave,
  onCancel,
  saving = false,
  initialComment = "",
  testIdPrefix = "actual-picker",
}) {
  // Local state — Save is explicit, not on date click.
  const [editValue, setEditValue] = useState(
    initialValue ? initialValue.split("T")[0] : "",
  );
  const [editComment, setEditComment] = useState(initialComment);

  // Constraint: actual dates ≤ min(today, project.data_date).
  // Enforced CLIENT-SIDE here (and mirrored by the mobile view's
  // validateActualDates) so the user sees the limit before clicking. NOTE: the
  // backend enforces only "≤ data_date", and only when a data_date is SET
  // (HTTP 400); the "not after today" half is a client-side UX rule only.
  const todayStr = new Date().toISOString().split("T")[0];
  const dataDateStr = projectDataDate ? projectDataDate.split("T")[0] : null;
  const maxActualStr =
    dataDateStr && dataDateStr < todayStr ? dataDateStr : todayStr;
  const maxActualDate = useMemo(() => parseISO(maxActualStr), [maxActualStr]);

  // Constraint #9: when picking actual_finish, must be ≥ actual_start.
  const startStr = activity?.actual_start
    ? activity.actual_start.split("T")[0]
    : null;
  const minFinishDate = useMemo(
    () =>
      field === "actual_finish" && startStr ? parseISO(startStr) : null,
    [field, startStr],
  );

  const isDisabledDate = useCallback(
    (date) => {
      // Strip time so date-only comparison is correct.
      const day = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
      );
      const maxDay = new Date(
        maxActualDate.getFullYear(),
        maxActualDate.getMonth(),
        maxActualDate.getDate(),
      );
      if (day > maxDay) return true;
      if (minFinishDate) {
        const minDay = new Date(
          minFinishDate.getFullYear(),
          minFinishDate.getMonth(),
          minFinishDate.getDate(),
        );
        if (day < minDay) return true;
      }
      return false;
    },
    [maxActualDate, minFinishDate],
  );

  // Memoize parsed Date so the Calendar's `selected` prop has stable
  // identity across re-renders (same fix we applied to DateField).
  const selectedDate = useMemo(
    () => (editValue ? parseISO(editValue) : undefined),
    [editValue],
  );

  const fieldLabel =
    field === "actual_start" ? "Actual Start" : "Actual Finish";

  return (
    <>
      <CalendarComponent
        mode="single"
        selected={selectedDate}
        onSelect={(date) => {
          if (date) setEditValue(format(date, "yyyy-MM-dd"));
        }}
        disabled={isDisabledDate}
        initialFocus
      />
      {/* Footer caption explains the disabled future-dates rule.
          Customer rule (May 11 meeting): actual = recorded fact, never
          future. Cap is min(today, project.data_date). */}
      <div
        className="px-3 py-2 border-t bg-slate-50 text-[10px] text-slate-600 leading-snug"
        data-testid={`${testIdPrefix}-footer`}
      >
        <strong>{fieldLabel}</strong> can only be on or before{" "}
        <strong>{format(maxActualDate, "MMM d, yyyy")}</strong> —
        {dataDateStr && dataDateStr < todayStr
          ? " the project data date."
          : " today's date."}
        {" "}Use Expected Finish for forecasts.
      </div>
      <div className="px-3 py-2 border-t">
        <Input
          placeholder="Add comment (optional)"
          value={editComment}
          onChange={(e) => setEditComment(e.target.value)}
          className="h-7 text-[10px]"
          maxLength={500}
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testIdPrefix}-comment-${field}`}
        />
      </div>
      <div className="flex items-center justify-end gap-1 p-2 border-t">
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-[10px]"
          onClick={(e) => {
            e.stopPropagation();
            onCancel();
          }}
          data-testid={`${testIdPrefix}-cancel-btn`}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="h-6 text-[10px]"
          disabled={saving || !editValue}
          onClick={(e) => {
            e.stopPropagation();
            onSave(editValue, editComment);
          }}
          data-testid={`${testIdPrefix}-save-btn`}
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
        </Button>
      </div>
    </>
  );
});

export default ActualDatePickerBody;
